import React, { useState } from 'react';
import { 
  Modal, 
  Table, 
  Text, 
  Button, 
  Group, 
  Badge, 
  Stack,
  Flex,
  Divider,
  Alert,
  ScrollArea,
  ActionIcon,
  Tooltip,
  LoadingOverlay,
  Box,
  Checkbox
} from '@mantine/core';
import { 
  IconAlertCircle, 
  IconCheck, 
  IconX,
  IconExternalLink,
  IconEye,
  IconHeart,
  IconMessage,
  IconRepeat
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

const ThreadPreviewModal = ({ 
  opened, 
  onClose, 
  threads = [], 
  onConfirmUpdate,
  updateResult = null,
  isUpdating = false 
}) => {
  const { t } = useTranslation();
  const [selectedThreads, setSelectedThreads] = useState(new Set(threads.map(t => t.id)));

  const handleSelectAll = () => {
    if (selectedThreads.size === threads.length) {
      setSelectedThreads(new Set());
    } else {
      setSelectedThreads(new Set(threads.map(t => t.id)));
    }
  };

  const handleThreadSelect = (threadId) => {
    const newSelected = new Set(selectedThreads);
    if (newSelected.has(threadId)) {
      newSelected.delete(threadId);
    } else {
      newSelected.add(threadId);
    }
    setSelectedThreads(newSelected);
  };

  const handleConfirm = () => {
    const threadsToUpdate = threads.filter(t => selectedThreads.has(t.id));
    onConfirmUpdate(threadsToUpdate);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group>
          <Text size="lg" weight={600}>스레드 미리보기</Text>
          <Badge color="blue" variant="light">
            {threads.length}개 발견
          </Badge>
        </Group>
      }
      size="xl"
      padding="lg"
      closeOnClickOutside={!isUpdating}
      closeOnEscape={!isUpdating}
      withCloseButton={!isUpdating}
    >
      <Stack spacing="md">
        <LoadingOverlay visible={isUpdating} />
        
        {!updateResult && threads.length > 0 && (
          <>
            <Alert 
              icon={<IconAlertCircle size={16} />}
              color="blue"
              variant="light"
            >
              <Text size="sm">
                아래 스레드들이 새로 발견되었습니다. 
                업데이트할 스레드를 선택한 후 확인 버튼을 클릭하세요.
              </Text>
            </Alert>

            <Group position="apart">
              <Checkbox
                label="전체 선택"
                checked={selectedThreads.size === threads.length}
                indeterminate={selectedThreads.size > 0 && selectedThreads.size < threads.length}
                onChange={handleSelectAll}
              />
              <Text size="sm" color="dimmed">
                {selectedThreads.size}개 선택됨
              </Text>
            </Group>

            <Divider />

            <ScrollArea style={{ height: 400 }}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 40 }}></Table.Th>
                    <Table.Th>날짜</Table.Th>
                    <Table.Th>내용</Table.Th>
                    <Table.Th style={{ width: 200 }}>지표</Table.Th>
                    <Table.Th style={{ width: 40 }}></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {threads.map((thread) => (
                    <Table.Tr key={thread.id}>
                      <Table.Td>
                        <Checkbox
                          checked={selectedThreads.has(thread.id)}
                          onChange={() => handleThreadSelect(thread.id)}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {formatDate(thread.timestamp)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" style={{ maxWidth: 300 }}>
                          {truncateText(thread.text)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group spacing="xs" noWrap>
                          <Tooltip label="조회수">
                            <Badge leftSection={<IconEye size={12} />} variant="light">
                              {thread.views || 0}
                            </Badge>
                          </Tooltip>
                          <Tooltip label="좋아요">
                            <Badge leftSection={<IconHeart size={12} />} variant="light" color="red">
                              {thread.likes || 0}
                            </Badge>
                          </Tooltip>
                          <Tooltip label="답글">
                            <Badge leftSection={<IconMessage size={12} />} variant="light" color="blue">
                              {thread.replies || 0}
                            </Badge>
                          </Tooltip>
                          <Tooltip label="리포스트">
                            <Badge leftSection={<IconRepeat size={12} />} variant="light" color="green">
                              {thread.reposts || 0}
                            </Badge>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        {thread.permalink && (
                          <Tooltip label="스레드 보기">
                            <ActionIcon
                              component="a"
                              href={thread.permalink}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="subtle"
                            >
                              <IconExternalLink size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>

            <Divider />

            <Group position="right">
              <Button
                variant="subtle"
                onClick={onClose}
                disabled={isUpdating}
              >
                취소
              </Button>
              <Button
                onClick={handleConfirm}
                loading={isUpdating}
                disabled={selectedThreads.size === 0}
                leftSection={<IconCheck size={18} />}
              >
                선택한 {selectedThreads.size}개 스레드 업데이트
              </Button>
            </Group>
          </>
        )}

        {!updateResult && threads.length === 0 && (
          <Alert 
            icon={<IconAlertCircle size={16} />}
            color="yellow"
            variant="light"
          >
            <Text size="sm">
              선택한 기간에 새로운 스레드가 없습니다.
            </Text>
          </Alert>
        )}

        {updateResult && (
          <Box>
            <Alert 
              icon={updateResult.success ? <IconCheck size={16} /> : <IconX size={16} />}
              color={updateResult.success ? "green" : "red"}
              variant="light"
              mb="md"
            >
              <Text size="sm" weight={500}>
                {updateResult.success ? "업데이트 완료!" : "업데이트 실패"}
              </Text>
              {updateResult.error && (
                <Text size="xs" mt="xs">
                  에러: {updateResult.error}
                </Text>
              )}
            </Alert>

            {updateResult.success && (
              <Stack spacing="xs">
                <Group>
                  <Text size="sm">조회된 스레드:</Text>
                  <Badge>{updateResult.totalFetched}개</Badge>
                </Group>
                <Group>
                  <Text size="sm">새로 추가된 스레드:</Text>
                  <Badge color="green">{updateResult.uniqueAdded}개</Badge>
                </Group>
                <Group>
                  <Text size="sm">중복으로 건너뛴 스레드:</Text>
                  <Badge color="gray">{updateResult.duplicatesSkipped}개</Badge>
                </Group>
              </Stack>
            )}

            <Group position="right" mt="lg">
              <Button onClick={onClose}>
                닫기
              </Button>
            </Group>
          </Box>
        )}
      </Stack>
    </Modal>
  );
};

export default ThreadPreviewModal;